import { GetterTree } from 'vuex';
import { RootState } from '@/store/types';
import { TypeRecognitionStatus, ChatState } from './types';
import { Message, RoomUser, User } from '@/types';

export const getters: GetterTree<ChatState, RootState> = {
  me(state): User | undefined {
    return state.me;
  },

  isMeResolved(state): boolean {
    return state.isMeResolved;
  },

  isActive(state): boolean | undefined {
    return state.active;
  },

  getIsPausedRecognizing(state): boolean {
    return state.isPausedRecognizing;
  },

  getAllUsersMessages(state): Message[] {
    return state.messages;
  },

  getParticipants(state): RoomUser[] {
    const participants = state.users.filter((elem: RoomUser) => elem.id !== state.me?.id);

    return participants;
  },

  getIdGuestIsTyping(state): string {
    return state.idGuestIsTyping;
  },

  getAllMessagesCount(state): number {
    return state.allMessagesCount;
  },

  getStatusIsPausedGuest(state): boolean {
    const guestIsPaused = state.users.findIndex(
      (user: RoomUser) => user.paused && user.id !== state.me?.id,
    );
    if (guestIsPaused !== -1) {
      return true;
    } else {
      return false;
    }
  },

  getStatusIsPausedMe(state): boolean {
    return state.me ? state.me.paused : true;
  },

  getRecognitionStatus(state): TypeRecognitionStatus {
    return state.recognitionStatus;
  },

  getIsCameraDoubleWidth(state): boolean {
    return state.isCameraDoubleWidth;
  },

  getIsShowProblemRecognitionForDu(state, getters): boolean {
    const isPaused = getters.getStatusIsPausedMe;
    const isPausedRecognizing = getters.getIsPausedRecognizing;
    const isDuMode = getters.me.userMode === 'du';

    return isDuMode && !isPaused && !isPausedRecognizing;
  },

  getActiveModelName(state): string | null {
    const modelId = state.conversationInfo?.recognitionModelId;
    if (!modelId) return null;
    return state.availableModels.find((m) => m.id === modelId)?.name ?? null;
  },

  // True when the conversation cannot be used under the model-picker feature:
  // either no recognition model is persisted on the room, or the persisted one
  // is no longer among the configured models. Callers gate this on
  // isModelPickerEnabled and on there being an actual room.
  getIsConversationModelInvalid(state): boolean {
    const modelId = state.conversationInfo?.recognitionModelId;
    // No model persisted (legacy room) → invalid.
    if (!modelId) return true;
    // Configured list not loaded yet: cannot judge a present id, so don't
    // false-positive (avoids a premature redirect before getRecognitionModels
    // resolves). The no-model case above is already covered.
    if (!state.availableModels.length) return false;
    // Model id present but no longer configured → stale/invalid.
    return !state.availableModels.some((m) => m.id === modelId);
  },
};
